import {Injectable} from '@angular/core';
import {BaseService} from './base-service';
import {HttpClient} from '@angular/common/http';
import {HttpUtilityService, PagingParams} from './http-utility-service';
import {SiteId} from './access-sites.interface';
import {Observable, map} from 'rxjs';
import {User, UserId} from '@app/layout/admin/services/api/user-interfaces';
import {ClassTransformer} from 'class-transformer';
import {UserService} from '@app/layout/admin/services/users/user.service';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {EquipmentId, MetadataTypeId} from '@app/graphql-typings/graphql';
import {ImpactId, PriorityId} from '@app/shared/api/model/type-ids';
import {TemplateId} from '@app/layout/rules/template-editor/api/template';
import {PagedModel} from '@app/shared/api/model/paged-model';
import {CollectionUtils} from '@app/shared/util/collections/collection-utils';
import {ObjectUtils} from '@app/shared/util/general/object-utils';
import {LoggedInUser} from './logged-in-user';
import {AlertNotificationCache} from '@app/layout/notifications/alert-notification/alert-notification-cache';
import {AlertDataNavigationState} from './tickets/tickets.interface';
import {TicketGenerationConfig, TicketGenerationConfigId} from '@shared/services/api/ticket-generation.service';

export interface AlertNotificationPreferences extends Record<SiteId, SiteNotificationPreferenceValue | null> {}

export interface NotificationPreferenceSearchTerm {
  site_notification_preference_id?: SitesNotificationPreferenceId;
  site_notification_preference_ids?: SitesNotificationPreferenceId[];
  site_id?: SiteId;
  site_ids?: SiteId[];
  user_id?: UserId;
  user_ids?: UserId[];
  type?: SiteNotificationType;
  types?: SiteNotificationType[];
}

export interface AlertNotificationNavigationState extends AlertDataNavigationState {
  dataCache?: AlertNotificationCache;
}

export enum AlertNotificationsPageParamName {
  SITE_ID = 'site_id',
  NOTIFICATION_ID = 'notification_id'
}

export const alertNotificationUpsertSiteQueryParamName = 'site_id';
export const alertNotificationsRoute = ['/notifications', 'alerts'];
export const alertNotificationsCreateRoute = [...alertNotificationsRoute, 'create'];
export const buildAlertNotificationsEditRoute: (id: SiteNotificationId) => any[] = id => [...alertNotificationsRoute, id, 'edit'];
export const buildAlertNotificationRoute = (siteId: SiteId) => ({
  route: ['/notifications', 'alerts'],
  queryParams: siteId != null ? {[AlertNotificationsPageParamName.SITE_ID]: siteId} : null
});


export enum AlertEventType {
  CREATED = 'alert.created',
  UPDATED = 'alert.updated'
}

export enum NotificationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export enum SiteNotificationPreferenceValue {
  ALL_NOTIFICATIONS = 'ALL_NOTIFICATIONS',
  NO_NOTIFICATIONS = 'NO_NOTIFICATIONS'
}

export enum SiteNotificationType {
  ALERT = 'alert'
}

interface RecipientNotificationPreference {
  site_id: SiteId;
  event_category: SiteNotificationType;
  preference: SiteNotificationPreferenceValue;
}

export type SitesNotificationPreferenceId = string;

export interface SiteNotificationPreference {
  sites_notification_preference_id: SitesNotificationPreferenceId;
  site_id: SiteId;
  user_id: UserId;
  type: SiteNotificationType;
  value: SiteNotificationPreferenceValue;
}

interface ApiAlertNotification {
  sites_notification_id: number;
  site_ids: number[];
  name: string;
  event_types: AlertEventType[];
  filter?: {
    metadata_type_ids?: MetadataTypeId[];
    equipment_ids?: EquipmentId[];
    impact_ids?: ImpactId[];
    priority_ids?: PriorityId[];
    template_ids?: TemplateId[];
  };
  status: NotificationStatus;
  recipient_user_ids: string[];
  recipients?: User & {preferences: RecipientNotificationPreference[]}[];
  author_user_id: string;
  create_escalated_ticket: boolean;
  ticket_generation_config_id: TicketGenerationConfigId | null;
}

export interface AlertNotificationRecipient {
  user: User;
  preferenceBySiteId: Map<SiteId, SiteNotificationPreferenceValue>;
}

export type SiteNotificationId = number;

export interface AlertNotification {
  sites_notification_id: SiteNotificationId;
  site_ids: SiteId[];
  name: string;
  event_types: AlertEventType[];
  filter?: {
    metadata_type_ids?: MetadataTypeId[];
    equipment_ids?: EquipmentId[];
    impact_ids?: ImpactId[];
    priority_ids?: PriorityId[];
    template_ids?: TemplateId[];
  };
  status: NotificationStatus;
  recipient_user_ids: UserId[];
  recipients?: AlertNotificationRecipient[];
  author_user_id: string;
  create_escalated_ticket: boolean;
  ticket_generation_config_id: TicketGenerationConfigId | null;
  ticket_generation_config?: TicketGenerationConfig | null;
}

export interface AlertNotificationSearchTerm {
  sites_notification_id?: SiteNotificationId;
  sites_notification_ids?: SiteNotificationId[];
  site_id?: SiteId;
  site_ids?: SiteId[];
  event_type?: AlertEventType;
  event_types?: AlertEventType[];
  status?: NotificationStatus;
  statuses?: NotificationStatus[];
  author_user_id?: UserId;
  author_user_ids?: UserId[];
  recipient_user_id?: UserId;
  recipient_user_ids?: UserId[];
  has_filter?: boolean;
  select_recipients?: boolean;
  fetch_recipients?: boolean;
  fetch_recipient_preferences?: boolean;
}

const DATA_KEY = 'sites_notification';
const DATA_KEY_PLURAL = 'sites_notifications';
const PREFERENCES_DATA_KEY_PLURAL = 'site_notification_preferences';

export interface UpsertAlertNotificationRequest {
  [DATA_KEY_PLURAL]: AlertNotification[];
}


@Injectable({providedIn: 'root'})
export class NotificationService extends BaseService {
  private readonly URL_PART = '/notifications/sites-notifications';
  private readonly PAGE_SIZE = 500;
  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              private classTransformer: ClassTransformer) {
    super();
  }

  searchAlertNotifications(pagingParams: PagingParams, searchTerm: AlertNotificationSearchTerm): Observable<PagedModel<AlertNotification[]>> {
    const params = {...pagingParams, ...searchTerm};
    return this.http.get<APIResponse<{[DATA_KEY_PLURAL]: AlertNotification[]}>>(
      this.getGateway() + this.URL_PART,
      {headers: this.getHeaders(), params}
    ).pipe(
      map(response => {
        const pagedResponse = this.httpUtilityService.extractPagedResponse<ApiAlertNotification[]>(response, DATA_KEY_PLURAL);
        return ({...pagedResponse, data: this.mapToInstance(pagedResponse.data)});
      })
    );
  }

  getRecipients(siteIds: SiteId[]): Observable<User[]> {
    return this.http.get(
      this.getGateway() + this.URL_PART + '/available-recipients',
      {headers: this.getHeaders(), params: {site_ids: siteIds, include_entity: true}}
    ).pipe(
      // TODO: KQ: use extract method when endpoint has been adjusted to return empty string
      // manually getting data instead of extract method because data is possibly null
      map((response): User[] => {
        const recipients = response[this.DATA_PREFIX]['recipient_users'] ?? [];
        return this.classTransformer.plainToInstance(UserService.modelMapping['User'], recipients);
      })
    );
  }

  upsertAlertNotifications(alertNotifications: AlertNotification[]): Observable<AlertNotification[]> {
    const requestBody: UpsertAlertNotificationRequest = {[DATA_KEY_PLURAL]: alertNotifications};
    return this.http.post<APIResponse<{[DATA_KEY_PLURAL]: AlertNotification[]}>>(
      this.getGateway() + this.URL_PART,
      requestBody,
      {headers: this.getHeaders()}
    ).pipe(
      map(response => {
        const updatedAlertNotificationData = this.httpUtilityService.extractResponseData<ApiAlertNotification[]>(response, DATA_KEY_PLURAL);
        return this.mapToInstance(updatedAlertNotificationData);
      })
    );
  }

  setNotificationStatus(id: SiteNotificationId, status: NotificationStatus): Observable<AlertNotification> {
    return this.http.patch<APIResponse<{[DATA_KEY]: AlertNotification}>>(
      this.getGateway() + this.URL_PART + '/' + id,
      {status},
      {headers: this.getHeaders()}
    ).pipe(
      map(response => {
        const updatedAlertNotificationData = this.httpUtilityService.extractResponseData<ApiAlertNotification>(response, DATA_KEY);
        return this.mapToInstance(updatedAlertNotificationData);
      })
    );
  }

  deleteAlertNotification(id: SiteNotificationId): Observable<boolean> {
    return this.http.delete(
      this.getGateway() + this.URL_PART + '/' + id,
      {headers: this.getHeaders()}
    ).pipe(map((response) => this.httpUtilityService.extractResponseData<{deleted: boolean}>(response, DATA_KEY).deleted));
  }

  hasAlertNotificationWriteAccess(user: LoggedInUser): boolean {
    return user && (user.isPlatformAdmin() || user.isCustomerUser() || user.isClientUser());
  }

  hasAlertNotificationReadAccess(user: LoggedInUser): boolean {
    return this.hasAlertNotificationWriteAccess(user) || user?.isContractorUser();
  }

  getAllNotificationPreferences(searchTerm: NotificationPreferenceSearchTerm): Observable<SiteNotificationPreference[]> {
    return this.httpUtilityService.pageUntilComplete(
      (pageInfo) => this.getNotificationsPreferences({start_index: pageInfo.first, limit: pageInfo.rows}, searchTerm),
      {first: 0, rows: this.PAGE_SIZE}
    );
  }

  upsertNotificationPreferences(updatedSiteNotificationPreferences: SiteNotificationPreference[]): Observable<SiteNotificationPreference[]> {
    return this.http.post<APIResponse<{[PREFERENCES_DATA_KEY_PLURAL]: SiteNotificationPreference[]}>>(
      this.getGateway() + this.URL_PART + '/preferences',
      {[PREFERENCES_DATA_KEY_PLURAL]: updatedSiteNotificationPreferences},
      {headers: this.getHeaders()}
    ).pipe(map(response => this.httpUtilityService.extractResponseData<SiteNotificationPreference[]>(response, PREFERENCES_DATA_KEY_PLURAL)));
  }

  private getNotificationsPreferences(pagingParams: PagingParams, searchTerm: NotificationPreferenceSearchTerm) {
    const params = {...pagingParams, ...searchTerm};
    return this.http.get<APIResponse<{[PREFERENCES_DATA_KEY_PLURAL]: SiteNotificationPreference[]}>>(
      this.getGateway() + this.URL_PART + '/preferences',
      {headers: this.getHeaders(), params}
    ).pipe(map(response => this.httpUtilityService.extractPagedResponse<SiteNotificationPreference[]>(response, PREFERENCES_DATA_KEY_PLURAL)));
  }

  private mapToInstance(alertNotification: ApiAlertNotification[]): AlertNotification[];
  private mapToInstance(alertNotification: ApiAlertNotification): AlertNotification;
  private mapToInstance(apiAlertNotification: ApiAlertNotification | ApiAlertNotification[]): AlertNotification | AlertNotification[] {
    return ObjectUtils.mapObjectOrArray(
      apiAlertNotification,
      apiAlertNotificationItem => {
        const alertNotificationInstance: AlertNotification = <any>{...apiAlertNotificationItem};
        if (apiAlertNotificationItem.recipients?.length) {
          alertNotificationInstance.recipients = apiAlertNotificationItem.recipients.map(apiRecipient => ({
            user: this.classTransformer.plainToInstance(UserService.modelMapping.User, apiRecipient),
            preferenceBySiteId: CollectionUtils.indexBy(
              apiRecipient.preferences?.filter(preference => preference.event_category === SiteNotificationType.ALERT),
              'site_id',
              notificationPreference => notificationPreference.preference
            )
          }));
        }
        return alertNotificationInstance;
      }
    );
  }
}
